/**
 * 发薪日工作流的**口径内核**（F 域 **F3** · `DR-052` · 批 87）
 *
 * 依据：`F域PRD` §4.7（触发 / 预演 / 落账 / 两个可花）· §7.3（`AC-F3.1`–`F3.16`）·
 * `DR-016` §3（工资条结构）· `DR-050`（I 域攒钱）· `DR-010` 决策三（两个「可花」分名）
 *
 * ## 🔴 三件只有本文件能做的事
 *
 * | 事 | 为什么必须在这里 |
 * |---|---|
 * | **工资识别**（`recognizeSalary`） | `Q23` 的裁决点：单位 = 交易分组、金额 = 分组净流入（实发） |
 * | **自动攒额度**（`autoSaveAmountOf`） | 固定额与比例两种量纲的**唯一解释处**；端侧与服务端都读它 |
 * | **「本次分配后可花」**（`composePayday`） | `AC-F3.9`：**不得**与 J3「本月剩余可花」共用函数 |
 *
 * ## 🔴 本文件**不**做的事
 *
 * - 不算「必付」：那是 `nucleus/upcoming.ts` 的 `composeUpcoming()`（F2 / H / J3 三方唯一实现）
 * - 不生成腿：目标攒走 **I 域 `GoalService.move()`**（`DR-050` D2 的两条腿）
 * - 不判「余额够不够」：那是服务端在**调用 I 域之前**的前置校验（`DR-052` D5 的余额闸）
 */

import { floorDiv, toCents } from './money.js';
import type { Cents } from '../types/brand.js';
import { PaydayPolicy } from '../constants/index.js';
import { AutoSaveMode } from '../enums/plan.js';

// ═══════════════════════════ ① 工资识别（`Q23` 的裁决落地） ═══════════════════════════

/** 识别只需要腿的这三样（**不传整条腿** —— 内核不依赖 ORM 形状） */
export interface SalaryLegInput {
  readonly direction: 'in' | 'out';
  readonly amount: number;
  /** `null` = 未分类 */
  readonly categoryId: string | null;
}

export interface SalaryRecognition {
  /** `true` ⇒ 这笔交易是一次「工资到账」，可以启动 F3 */
  readonly isSalary: boolean;
  /**
   * **分组净流入**（实发）—— `Σ in − Σ out`，按交易币种的整数分。
   *
   * 🔴 只有在 `isSalary === true` 时才是"可分配的钱"；否则为 0。
   */
  readonly netCents: Cents;
  /** 不识别时的原因（人话；识别成功为 `null`） */
  readonly reason: string | null;
}

/** 该分类是否属于**工资白名单**（`DR-052` D1；白名单在 `PaydayPolicy`，只改一处） */
export function isSalaryCategory(categoryId: string | null): boolean {
  if (categoryId === null) return false;
  return (PaydayPolicy.SalaryCategoryIds as readonly string[]).includes(categoryId);
}

/**
 * 识别一笔交易是不是「工资到账」，并算出**可分配金额**。
 *
 * ## 🔴 判据（两张表读过才敢写这一句）
 *
 * 1. **单位 = 交易分组**（不是单腿）：`DR-016` §3 的完整模式下，同一交易里既有
 *    `银行 in 税前` 的收入腿，也有社保/个税/去向等 `out` 腿 ⇒ 逐腿看会拿到**税前**。
 * 2. **条件 = 存在收入腿，且其分类 ∈ 工资白名单**。
 * 3. **金额 = 分组净流入**（`Σ in − Σ out`）：完整模式 = 实发；简化模式 = 腿额（净额退化）。
 * 4. **净流入必须为正**：代扣合计 ≥ 收入（数据异常）时不触发，避免"分配一个负数"。
 *
 * ## 为什么不是「收入腿金额」
 *
 * `AC-F3.8` 的公式是「工资到账 − 必付 − 目标攒」。完整模式下只有**实发**能拿去分配；
 * 用税前会把**根本不存在的钱**分配出去（`DR-052` §1 的第一行）。
 */
export function recognizeSalary(legs: readonly SalaryLegInput[]): SalaryRecognition {
  const income = legs.filter((l) => l.direction === 'in');
  const salaryLeg = income.find((l) => isSalaryCategory(l.categoryId));
  if (salaryLeg === undefined) {
    return {
      isSalary: false,
      netCents: toCents(0),
      reason: income.length === 0 ? '这笔交易没有收入' : '这笔收入不在「工资」分类下',
    };
  }

  const inflow = legs.reduce((s, l) => (l.direction === 'in' ? s + l.amount : s), 0);
  const outflow = legs.reduce((s, l) => (l.direction === 'out' ? s + l.amount : s), 0);
  const net = inflow - outflow;

  if (net <= 0) {
    return {
      isSalary: false,
      netCents: toCents(0),
      reason: `这笔工资的净流入不为正（收入 ${String(inflow)} 分、代扣 ${String(outflow)} 分）——请检查工资条的代扣子项`,
    };
  }

  return { isSalary: true, netCents: toCents(net), reason: null };
}

// ═══════════════════════════ ② 自动攒额度 ═══════════════════════════

/** 目标上的自动攒配置（`savings_goals.auto_save_mode` / `auto_save_value`） */
export interface AutoSaveConfig {
  readonly mode: AutoSaveMode;
  /** `fixed` ⇒ 分；`percent` ⇒ E4（10000 = 100%）；`off` ⇒ 0 */
  readonly value: number;
}

export const EMPTY_AUTO_SAVE: AutoSaveConfig = { mode: AutoSaveMode.Off, value: 0 };

/**
 * 本期该给这个目标攒多少（**整数分**）。
 *
 * | 模式 | 计算 | 上限 |
 * |---|---|---|
 * | `off` | `0` | — |
 * | `fixed` | `value` | 目标**剩余额** |
 * | `percent` | `floor(net × value / 10000)`（**向下取整到分**） | 目标剩余额 |
 *
 * 🔴 **向下取整**（`floorDiv`）而不是四舍五入：自动攒多攒 1 分会让"可花"少 1 分，
 * 而少攒 1 分只是留在卡里 —— 宁可少不多（与 `DR-023` 的"不虚增"一致）。
 * 🔴 **封顶到剩余额**：目标还差 ¥200 时按比例算出 ¥300，只能攒 ¥200（否则进度会超额）。
 *    `remainingCents ≤ 0` ⇒ 0（已达成的目标不再自动攒）。
 */
export function autoSaveAmountOf(input: {
  readonly config: AutoSaveConfig;
  /** 本次工资的**实发净额**（`recognizeSalary().netCents`） */
  readonly netCents: number;
  /** 目标还差多少（`targetCents − savedCents`，可为负） */
  readonly remainingCents: number;
}): Cents {
  const { config, netCents, remainingCents } = input;
  if (remainingCents <= 0) return toCents(0);
  if (config.mode === AutoSaveMode.Off) return toCents(0);

  const raw =
    config.mode === AutoSaveMode.Fixed
      ? config.value
      : floorDiv(netCents * config.value, PaydayPolicy.PercentMaxE4);

  if (raw <= 0) return toCents(0);
  return toCents(raw > remainingCents ? remainingCents : raw);
}

/**
 * 自动攒配置的合法性（**服务端裁决、端侧即时提示共用**）。
 *
 * 返回空数组 = 合法。`remainingCents` 缺省时只校验形态（端侧新建目标时还没有"剩余额"）。
 */
export function checkAutoSave(input: {
  readonly config: AutoSaveConfig;
  readonly remainingCents?: number;
}): string[] {
  const { config, remainingCents } = input;
  const issues: string[] = [];

  if (
    config.mode !== AutoSaveMode.Off &&
    config.mode !== AutoSaveMode.Fixed &&
    config.mode !== AutoSaveMode.Percent
  ) {
    issues.push('自动攒模式只能是「不自动攒 / 每期固定额 / 按比例」');
    return issues;
  }

  if (!Number.isInteger(config.value)) {
    issues.push('自动攒的额度必须是整数（固定额按分、比例按万分之一）');
    return issues;
  }

  if (config.mode === AutoSaveMode.Off) {
    if (config.value !== 0) issues.push('选择「不自动攒」时额度必须为 0');
    return issues;
  }

  if (config.mode === AutoSaveMode.Fixed) {
    if (config.value < PaydayPolicy.MinFixedSaveCents) {
      issues.push(`每期固定额至少 ${String(floorDiv(PaydayPolicy.MinFixedSaveCents, 100))} 元`);
    }
  } else {
    if (config.value < 1 || config.value > PaydayPolicy.PercentMaxE4) {
      issues.push('比例必须在 0.01% ~ 100% 之间');
    }
  }

  if (remainingCents !== undefined && config.value > 0 && config.mode === AutoSaveMode.Fixed) {
    if (config.value > remainingCents) {
      issues.push('固定额不得超过目标当前还差的钱');
    }
  }

  return issues;
}

// ═══════════════════════════ ③ 「本次分配后可花」 ═══════════════════════════

/** 预演里的一项分配（目标 → 金额） */
export interface PaydayAllocation {
  readonly goalId: string;
  readonly amountCents: number;
}

export interface PaydayComposeResult {
  /** 勾选项的合计（分） */
  readonly plannedTotalCents: Cents;
  /** 🔴 `AC-F3.8`：`实发 − 必付 − 目标攒`（**可为负** —— 负数说明这笔钱不够分） */
  readonly distributableCents: Cents;
  /** 分配合计是否超过可分配额 */
  readonly overCommitted: boolean;
  /** 提示（**只提示不拦截** —— 与预算的软提示同口径） */
  readonly issues: readonly string[];
}

/**
 * 组装「本次分配后可花」（**F3 唯一实现** · `AC-F3.9`）。
 *
 * ## 🔴 它与 J3「本月剩余可花」是**两个名字、两条口径**，不得互相调用
 *
 * | | 输入 | 语义 |
 * |---|---|---|
 * | F3（本函数） | **一笔工资**的实发 | 「这次发的钱，分完之后还剩多少」 |
 * | J3 | 预算 − 已花 − 必付未付 | 「这个月还能花多少」（与这笔工资无关） |
 *
 * 两者会给出不同的数字，**这是对的**：F3 是发薪日一次性，J3 是随时可看。
 * `AC-F3.9` 要求代码审计能证明"没有共用函数" ⇒ 本函数**不 import** J 域任何东西。
 *
 * ## 为什么 `distributable` 允许为负
 *
 * 「必付 > 实发」或"用户勾得比钱多"是**真实会发生**的（房贷月供 8,000、工资 7,500）。
 * 用 `0` 截断会掩盖"这个月缺口多少"——那正是用户最需要看到的数字。
 */
export function composePayday(input: {
  readonly netCents: number;
  readonly payableCents: number;
  readonly allocations: readonly PaydayAllocation[];
}): PaydayComposeResult {
  const planned = input.allocations.reduce((s, a) => s + a.amountCents, 0);
  const distributable = input.netCents - input.payableCents - planned;

  const issues: string[] = [];
  if (input.payableCents > input.netCents) {
    issues.push(
      `本月必付（${yuanText(input.payableCents)}）已经超过这笔工资（${yuanText(input.netCents)}）`,
    );
  }
  if (distributable < 0) {
    issues.push(`分配的金额超过了这笔工资能覆盖的范围，缺口 ${yuanText(-distributable)}`);
  }

  return {
    plannedTotalCents: toCents(planned),
    distributableCents: toCents(distributable),
    overCommitted: distributable < 0,
    issues,
  };
}

/**
 * 分 → 「¥1,234.56」的短文本（**只用于内核生成的提示句**）。
 *
 * ⚠️ 端侧渲染金额必须走 `formatMoneyText()`（带币种）；这里不引币种，因为提示句里的
 * 币种由外层视图的 `currency` 统一表达。
 */
function yuanText(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = cents < 0 ? -cents : cents;
  const whole = floorDiv(abs, 100);
  const frac = abs - whole * 100;
  return `${sign}¥${String(whole)}.${String(frac).padStart(2, '0')}`;
}
